import { useMemo, useState } from "react";
import { useQuery } from "../../api/useQuery.js";
import { getSubjectList } from "../../api/queries.js";
import { SubjectPanel } from "../../components/subjects/SubjectPanel.js";
import { SearchInput } from "../../components/ui/SearchInput.js";
import { SegmentedControl } from "../../components/ui/SegmentedControl.js";
import { PageTitle } from "../../components/ui/PageTitle.js";
import { QueryStatus } from "../../components/ui/LoadingPlaceholders.js";
import { Button, type MessageKey, useT } from "@mmm/ui";
type StatusFilter = "all" | "active" | "archived";
type SortOption = "nameAsc" | "nameDesc" | "recent" | "color";

// Labels travel as message keys and are translated at render, so the options
// repaint when the UI language changes.
const STATUS_OPTIONS: Array<{ value: StatusFilter; labelKey: MessageKey }> = [
  { value: "all", labelKey: "common.all" },
  { value: "active", labelKey: "common.active" },
  { value: "archived", labelKey: "common.archived" },
];

const SORT_OPTIONS: Array<{ value: SortOption; labelKey: MessageKey }> = [
  { value: "nameAsc", labelKey: "subjects.sortNameAsc" },
  { value: "nameDesc", labelKey: "subjects.sortNameDesc" },
  { value: "recent", labelKey: "subjects.sortRecent" },
  { value: "color", labelKey: "common.color" },
];

export function SubjectListScreen() {
  const subjects = useQuery(getSubjectList, undefined);
  const t = useT();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortOption>("nameAsc");

  const all = subjects.data || [];
  const activeCount = all.filter((s) => s.status === "active").length;
  const archivedCount = all.length - activeCount;

  const filtered = useMemo(() => {
    let list = all;
    if (status !== "all") list = list.filter((s) => s.status === status);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) => s.title.toLowerCase().includes(q));
    }

    const sorted = [...list];
    if (sort === "nameAsc") sorted.sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "nameDesc") sorted.sort((a, b) => b.title.localeCompare(a.title));
    if (sort === "recent") sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    if (sort === "color") sorted.sort((a, b) => a.color.localeCompare(b.color));
    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjects.data, status, search, sort]);

  const loaded = Boolean(subjects.data);
  const subtitle = !loaded
    ? t("subjects.loading")
    : all.length === 0
      ? t("subjects.emptyTitle")
      : t("common.activeArchivedSummary", { active: activeCount, archived: archivedCount });
  const toolbar = (
    <>
      <SearchInput value={search} onChange={setSearch} placeholder={t("subjects.searchPlaceholder")} />
      <SegmentedControl
        options={STATUS_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
        value={status}
        onChange={setStatus}
      />
      <select className="mmm-select" value={sort} onChange={(event) => setSort(event.target.value as SortOption)}>
        {SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey)}
          </option>
        ))}
      </select>
    </>
  );
  const emptyAction =
    loaded && all.length > 0 ? (
      <Button
        variant="text"
        onClick={() => {
          setSearch("");
          setStatus("all");
        }}
      >
        {t("common.clearFilters")}
      </Button>
    ) : undefined;

  return (
    <div className="mmm-dashboard-screen">
      <PageTitle title={t("nav.subjects")} />
      <QueryStatus queries={[subjects]} />

      <SubjectPanel
        className="mmm-subjects-page-panel"
        title={t("subjects.allSubjects")}
        subtitle={subtitle}
        subjects={loaded ? filtered : []}
        loading={subjects.loading}
        hasError={Boolean(subjects.error) && !subjects.waiting}
        onRetry={subjects.retry}
        toolbar={toolbar}
        emptyTitle={all.length === 0 ? t("subjects.emptyTitle") : t("subjects.noMatch")}
        emptyMessage={all.length === 0 ? t("subjects.emptyMessage") : t("subjects.noMatchMessage")}
        emptyAction={emptyAction}
        addNavigateOnCreate={false}
        viewStorageKey="subjects-page"
      />
    </div>
  );
}
